/**
 * TaskClassifications - Página de gerenciamento de classificações de tarefas
 * 
 * Características:
 * - Filtro por categoria
 * - Suporte a hierarquia (parent_id)
 * - CRUD completo via Dialog
 * - Seleção de cor e toggle ativo/inativo
 * 
 * @author Sistema
 * @version 1.1.0
 * RELEASE: 2026-04-06 v1.0.0 - Criação da página
 * RELEASE: 2026-04-19 v1.1.0 - Alinhamento ao padrão de header e KPIs do módulo de Contatos
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Pencil,
  Trash2,
  Tags,
  Loader2,
  Search,
  MoreHorizontal,
  CheckCircle,
  ClipboardList,
  GitBranch,
  Globe,
} from 'lucide-react';

import { PremiumDialogHeader } from '@/components/shared/PremiumDialogHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useTaskClassifications, TaskClassification, UpsertTaskClassificationInput } from '@/hooks/useTaskClassifications';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { useTaskModuleType, useTaskModule } from '@/hooks/useTaskModuleType';
import { HexColorPicker } from '@/components/tasks/HexColorPicker';

/** Valor sentinela do Select para "classificação global do módulo" (category_id NULL) */
const GLOBAL_CATEGORY = '__global__';

// Cores disponíveis para seleção
const AVAILABLE_COLORS = [
  '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7',
  '#ec4899', '#f43f5e', '#ef4444', '#f97316',
  '#eab308', '#22c55e', '#14b8a6', '#06b6d4',
  '#0ea5e9', '#64748b', '#78716c', '#475569',
];


// ============================================================================
// COMPONENTE DE KPI
// ============================================================================

interface MetricCardProps {
  title: string;
  value: number;
  icon: React.ElementType;
  variant?: 'primary' | 'success' | 'info' | 'purple';
}

/** Mapeamento de variantes para cores (padrão unificado DealListKpiCards/Contatos) */
const classificationVariantStyles = {
  primary: { cardBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-700 dark:text-purple-300' },
  success: { cardBg: 'bg-lime-100 dark:bg-green-900/30', iconColor: 'text-green-700 dark:text-green-300' },
  info: { cardBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-700 dark:text-blue-300' },
  purple: { cardBg: 'bg-cyan-100 dark:bg-cyan-900/30', iconColor: 'text-cyan-700 dark:text-cyan-300' },
};

/** Card de KPI compacto horizontal (padrão unificado) */
function MetricCard({ title, value, icon: Icon, variant = 'primary' }: MetricCardProps) {
  const styles = classificationVariantStyles[variant];
  return (
    <Card className={cn('border-0 shadow-none transition-all hover:shadow-md', styles.cardBg)}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={cn('h-5 w-5 shrink-0', styles.iconColor)} />
            <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">{title}</p>
          </div>
          <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function TaskClassifications() {
  const { t } = useTranslation('common');
  const { categories, isLoading: isCategoriesLoading } = useTaskCategories();
  // Módulo corrente (normal | retention | legal) — define se há classificações globais
  const taskType = useTaskModuleType();
  // v1.3.0: metadados do módulo corrente (rótulos dinâmicos no header)
  const taskModule = useTaskModule();
  const isModuleTask = taskType !== 'normal';
  
  // Filtro de categoria selecionada
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const {
    classifications,
    isLoading,
    upsert,
    isUpserting,
    remove,
    isRemoving,
  } = useTaskClassifications(selectedCategoryId || undefined);


  // Estado do Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClassification, setEditingClassification] = useState<TaskClassification | null>(null);
  const [search, setSearch] = useState('');

  // Estado do formulário
  const [formCategoryId, setFormCategoryId] = useState('');
  const [formParentId, setFormParentId] = useState<string>('');
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formColor, setFormColor] = useState(AVAILABLE_COLORS[0]);
  const [formSortOrder, setFormSortOrder] = useState(0);
  const [formIsActive, setFormIsActive] = useState(true);

  // Dialog de confirmação
  const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();

  // KPIs
  const totalCount = classifications.length;
  const activeCount = classifications.filter(c => c.is_active).length;
  const parentCount = classifications.filter(c => !c.parent_id).length;
  const totalTasks = classifications.reduce((sum, c) => sum + c.tasks_count, 0);

  // Classificações filtradas por busca
  const filtered = classifications.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.description?.toLowerCase().includes(search.toLowerCase())
  );

  // Classificações raiz da categoria selecionada no form (para seleção de parent)
  // v1.2.0: no modo global, as "pais" possíveis também são as globais do módulo
  const parentOptions = classifications.filter(c =>
    (formCategoryId === GLOBAL_CATEGORY ? !c.category_id : c.category_id === formCategoryId)
    && !c.parent_id && c.id !== editingClassification?.id
  );

  /**
   * Abre o dialog para criar nova classificação
   */
  const handleCreate = () => {
    setEditingClassification(null);
    // Em Retenção/Jurídico o padrão é a classificação global (motivo do módulo)
    setFormCategoryId(
      isModuleTask ? GLOBAL_CATEGORY : (selectedCategoryId || categories[0]?.id || '')
    );
    setFormParentId('');
    setFormName('');
    setFormDescription('');
    setFormColor(AVAILABLE_COLORS[0]);
    setFormSortOrder(classifications.length + 1);
    setFormIsActive(true);
    setDialogOpen(true);
  };

  /**
   * Abre o dialog para editar classificação existente
   */
  const handleEdit = (classification: TaskClassification) => {
    setEditingClassification(classification);
    setFormCategoryId(classification.category_id || GLOBAL_CATEGORY);
    setFormParentId(classification.parent_id || '');
    setFormName(classification.name);
    setFormDescription(classification.description || '');
    setFormColor(classification.color || AVAILABLE_COLORS[0]);
    setFormSortOrder(classification.sort_order);
    setFormIsActive(classification.is_active);
    setDialogOpen(true);
  };

  /**
   * Salva a classificação
   */
  const handleSave = async () => {
    if (!formName.trim() || !formCategoryId) return;

    const isGlobal = formCategoryId === GLOBAL_CATEGORY;

    const input: UpsertTaskClassificationInput = {
      // Global: category_id NULL + task_type do módulo corrente
      p_category_id: isGlobal ? null : formCategoryId,
      p_task_type: isGlobal ? taskType : undefined,
      p_name: formName.trim(),
      p_description: formDescription.trim() || undefined,
      p_color: formColor,
      p_sort_order: formSortOrder,
      p_is_active: formIsActive,
      p_parent_id: formParentId || undefined,
    };

    if (editingClassification) {
      input.p_id = editingClassification.id;
    }

    await upsert(input);
    setDialogOpen(false);
  };


  /**
   * Exclui uma classificação com confirmação
   */
  const handleDelete = async (classification: TaskClassification) => {
    const confirmed = await confirmDialog({
      title: 'Excluir Classificação',
      description: `Deseja realmente excluir a classificação "${classification.name}"? Esta ação não pode ser desfeita.`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      variant: 'danger',
    });

    if (confirmed) {
      await remove(classification.id);
    }
  };

  // Estado de carregamento
  if (isLoading && isCategoriesLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-8 w-36" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-14" />)}
        </div>
        <Skeleton className="h-64 w-full mx-4" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header Compacto (padrão Contatos) */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Classificações de {taskModule.label}</h1>
          <p className="text-xs text-muted-foreground">
            {`Organize ${taskModule.label.toLowerCase()} com classificações hierárquicas`}
          </p>
        </div>
        <Button
          size="xs"
          onClick={handleCreate}
          disabled={categories.length === 0}
          className="hover:shadow-md hover:scale-[1.02] transition-all duration-200"
        >
          <Plus className="mr-1.5" />
          Nova Classificação
        </Button>
      </div>

      {/* KPIs (padrão horizontal compacto) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3">
        <MetricCard title="Total" value={totalCount} icon={Tags} variant="primary" />
        <MetricCard title="Ativas" value={activeCount} icon={CheckCircle} variant="success" />
        <MetricCard title="Grupos (raiz)" value={parentCount} icon={GitBranch} variant="info" />
        <MetricCard title="Tarefas" value={totalTasks} icon={ClipboardList} variant="purple" />
      </div>

      <div className="px-4 pb-4 space-y-4">
      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Select value={selectedCategoryId || 'all'} onValueChange={(v) => setSelectedCategoryId(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-full sm:w-64">
            <SelectValue placeholder="Todas as categorias" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as categorias</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full" style={{ backgroundColor: cat.color }} />
                  {cat.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar classificações..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Tabela de Classificações */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Tags className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-lg font-medium">Nenhuma classificação encontrada</p>
            <p className="text-sm mt-1">
              {categories.length === 0
                ? 'Crie uma categoria primeiro'
                : search
                  ? 'Tente ajustar sua busca'
                  : 'Crie a primeira classificação'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">Cor</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead className="hidden md:table-cell">Categoria</TableHead>
                <TableHead className="hidden lg:table-cell">Pai</TableHead>
                <TableHead className="text-center">Tarefas</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((classification) => (
                <TableRow key={classification.id}>
                  <TableCell>
                    <div
                      className="h-6 w-6 rounded-md"
                      style={{ backgroundColor: classification.color || '#64748b' }}
                    />
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">
                        {classification.parent_id && (
                          <span className="text-muted-foreground mr-1">└</span>
                        )}
                        {classification.name}
                      </p>
                      {classification.description && (
                        <p className="text-xs text-muted-foreground line-clamp-1">{classification.description}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {/* v1.2.0: classificação global do módulo não possui categoria */}
                    <Badge variant="outline" className="text-xs gap-1">
                      {!classification.category_id && <Globe className="h-3 w-3" />}
                      {classification.category_name || 'Todo o módulo'}
                    </Badge>
                  </TableCell>

                  <TableCell className="hidden lg:table-cell">
                    {classification.parent_name ? (
                      <span className="text-sm text-muted-foreground">{classification.parent_name}</span>
                    ) : (
                      <span className="text-sm text-muted-foreground/50">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="secondary" className="text-xs">
                      {classification.tasks_count}
                    </Badge>
                  </TableCell>
                  {/* Status — padrão unificado (Badge outline + StatusDot) */}
                  <TableCell className="text-center">
                    <Badge
                      variant="outline"
                      className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                    >
                      <StatusDot tone={classification.is_active ? 'emerald' : 'muted'} />
                      {classification.is_active ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(classification)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDelete(classification)}
                          className="text-destructive focus:text-destructive"
                          disabled={classification.tasks_count > 0 || classification.children_count > 0}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      </div>


      {/* Dialog Criar/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col">
          {/* Cabeçalho padrão premium (mesmo do modal "Nova anotação") */}
          <PremiumDialogHeader
            icon={Tags}
            title={editingClassification ? 'Editar Classificação' : 'Nova Classificação'}
            description={
              editingClassification
                ? 'Atualize os dados da classificação'
                : 'Preencha os dados para criar uma nova classificação'
            }
          />

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            {/* Categoria (v1.2.0: opção global para módulos Retenção/Jurídico) */}
            <div className="space-y-2">
              <Label>Categoria *</Label>
              <Select value={formCategoryId} onValueChange={(v) => { setFormCategoryId(v); setFormParentId(''); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a categoria" />
                </SelectTrigger>
                <SelectContent>
                  {isModuleTask && (
                    <SelectItem value={GLOBAL_CATEGORY}>
                      <div className="flex items-center gap-2">
                        <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                        Vale para todo o módulo
                      </div>
                    </SelectItem>
                  )}
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full" style={{ backgroundColor: cat.color }} />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isModuleTask && formCategoryId === GLOBAL_CATEGORY && (
                <p className="text-xs text-muted-foreground">
                  A classificação ficará disponível em todas as categorias deste módulo e
                  não será perdida quando a tarefa mudar de categoria.
                </p>
              )}
            </div>


            {/* Classificação Pai */}
            {parentOptions.length > 0 && (
              <div className="space-y-2">
                <Label>Classificação Pai (opcional)</Label>
                <Select value={formParentId || 'none'} onValueChange={(v) => setFormParentId(v === 'none' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Nenhuma (raiz)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma (raiz)</SelectItem>
                    {parentOptions.map((parent) => (
                      <SelectItem key={parent.id} value={parent.id}>
                        {parent.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Nome */}
            <div className="space-y-2">
              <Label htmlFor="class-name">Nome *</Label>
              <Input
                id="class-name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Ex: Bug, Feature, Melhoria..."
                maxLength={100}
              />
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="class-description">Descrição</Label>
              <Textarea
                id="class-description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Descrição opcional"
                rows={2}
                maxLength={500}
              />
            </div>

            {/* Cor, Ordem e Status */}
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <HexColorPicker value={formColor} onChange={setFormColor} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="class-sort-order">Ordem</Label>
                <Input
                  id="class-sort-order"
                  type="number"
                  value={formSortOrder}
                  onChange={(e) => setFormSortOrder(parseInt(e.target.value) || 1)}
                  min={1}
                />
              </div>
              {editingClassification && (
                <div className="flex items-center gap-2 pb-2">
                  <Switch
                    id="class-active"
                    checked={formIsActive}
                    onCheckedChange={setFormIsActive}
                  />
                  <Label htmlFor="class-active" className="text-sm">Ativa</Label>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="border-t bg-muted/30 px-6 py-3">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={!formName.trim() || !formCategoryId || isUpserting}
            >
              {isUpserting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingClassification ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação */}
      {ConfirmDialogComponent}
    </div>
  );
}
